import type { AxiosError } from "axios";
import type { UseQueryResult } from "react-query";
import { useMutation, useQuery } from "react-query";

import { AnalyticsGA } from "../../utils/AnalyticsGA";
import apiBase from "../../utils/apiBase";

export interface IRegularBillingInfo {
  maxQuantityPerBillingPeriod: number;
  billingPeriodLimit: number;
  subscriptionLimit: number;
  current: number;
}

export interface InfoByModel {
  inputTokens: number;
  outputTokens: number;
  inputTokensPrice: number;
  inputTokensPriceWithDiscount: number;
  outputTokensPrice: number;
  outputTokensPriceWithDiscount: number;
}

export interface IAppMetrics {
  cogCachePrice: number;
  costSavings: number;
  totalActiveInputTokensUsed: number;
  totalActiveOutputTokensUsed: number;
  totalCachedInputTokensUsed: number;
  totalCachedOutputTokensUsed: number;
  openAiRequestCount: number;
  cachedRequestCount: number;
  perModel: {
    [key: string]: {
      cached: InfoByModel;
      active: InfoByModel;
      costSavings: number;
      cogCachePrice: number;
      azureOpenAiPrice: number;
    };
  };
}
export interface IBillingSettings {
  isAutoChargeEnabled: boolean;
  expectedAmountOnTheAccount: number;
  chargeWhen: number;
}

export interface IBillingSettingsWithValues extends IBillingSettings {
  maxChargeWhenValue: number;
  maxExpectedAmountValue: number;
  minChargeWhenValue: number;
  minExpectedAmountValue: number;
  minimalExpectedTransaction: number;
  lowCreditBalanceThreshold: number;
}
export interface IPTUBillingInfo {
  credits?: number;
  amountDue?: number;
  freeCredits?: number;
  totalFreeCredits?: number;
  currentTierId?: number;
  billingSettings?: IBillingSettingsWithValues;
  tokenMetrics: IAppMetrics;
}

export interface ICheckoutLink {
  url: string;
}

export interface IBillingInfo {
  type: string;
  subscriptionId: string;
  subscriptionName: string;
  planId: string;
  planName: string;
  status: string;
  provider: string;
  regularBillingInfo?: IRegularBillingInfo;
  ptuBillingInfo?: IPTUBillingInfo;
}

const fetchBillingInfo = async (organizationId: string | undefined) => {
  return await apiBase.get(`/organizations/${organizationId}/billing`);
};

const fetchBillingSettings = async (organizationId: string | undefined) => {
  return await apiBase.get(`/organizations/${organizationId}/billing-settings`);
};

const patchBillingSettings = async (
  organizationId: string,
  settings: IBillingSettings,
) => {
  return await apiBase.patch(
    `/organizations/${organizationId}/billing-settings`,
    settings,
  );
};

const fetchCheckoutLink = async (
  organizationId: string | undefined,
  returnUrl: string,
) => {
  return await apiBase.get(`/organizations/${organizationId}/checkout`, {
    params: {
      returnUrl,
    },
  });
};

const fetchCustomerPortalLink = async (
  organizationId: string | undefined,
  returnUrl: string,
) => {
  return await apiBase.get(`/organizations/${organizationId}/customer-portal`, {
    params: {
      returnUrl,
    },
  });
};

export const useBilling = (
  organizationId: string | undefined,
): UseQueryResult<IBillingInfo, AxiosError> => {
  return useQuery(
    ["billing", organizationId],
    () => fetchBillingInfo(organizationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const useBillingSettings = (
  organizationId: string | undefined,
): UseQueryResult<IBillingSettingsWithValues, unknown> => {
  return useQuery(
    ["billing-settings", organizationId],
    () => fetchBillingSettings(organizationId),
    {
      enabled: !!organizationId,
    },
  );
};

export const usePatchBillingSettings = () => {
  return useMutation({
    mutationFn: ({
      organizationId,
      settings,
    }: {
      organizationId: string;
      settings: IBillingSettings;
    }) => {
      return patchBillingSettings(organizationId, settings);
    },
  });
};

export const useCheckoutLink = (
  organizationId: string | undefined,
  returnUrl: string,
): UseQueryResult<ICheckoutLink, unknown> => {
  return useQuery(
    ["billing-checkout-link", organizationId, returnUrl],
    () => fetchCheckoutLink(organizationId, returnUrl),
    {
      enabled: false,
      onSuccess: () => {
        AnalyticsGA.trackEvent(
          "Checkout",
          "Go to Checkout",
          "User clicked on Go to Checkout",
        );
      },
    },
  );
};

export const useCustomerPortalLink = (
  organizationId: string | undefined,
  returnUrl: string,
): UseQueryResult<ICheckoutLink, unknown> => {
  return useQuery(
    ["billing-customer-portal-link", organizationId, returnUrl],
    () => fetchCustomerPortalLink(organizationId, returnUrl),
    {
      enabled: false,
    },
  );
};
